import React from 'react';
import { X, Camera, Star, MapPin, Award, CheckCircle, CreditCard } from 'lucide-react';
import '../../../styles/bookingWidget.css';
import { formatDisplayMoney } from '../../../utils/formatMoney';

const MobileBookingSidebar = ({
  isOpen,
  onClose,
  onOpen,
  vendor,
  category,
  service,
  serviceRating,
  serviceReviews,
  bookingData,
  calculateCommission,
  calculateTax,
  calculateTotal,
  calculateDepositAmount,
  calculatePaymentTerms,
  commissionSetting,
  showTaxLine = false,
  appliedDiscount,
  displayCurrency = 'USD',
  displayLocale = 'en-US',
}) => {
  const paymentTerms = calculatePaymentTerms?.();
  const money = (n) => formatDisplayMoney(n, displayCurrency, displayLocale);

  return (
    <>
      {!isOpen && (
        <div className="fixed bottom-5 right-5 z-40 lg:hidden">
          <button
            type="button"
            onClick={onOpen}
            className="booking-float-button relative flex h-12 w-12 items-center justify-center rounded-full bg-m-ink text-white shadow-md transition-colors hover:bg-neutral-800"
            aria-label="Open booking summary"
          >
            <CreditCard className="relative z-10 h-5 w-5" />
            <span className="booking-ping-animation absolute inset-0 rounded-full bg-m-ink opacity-30" />
          </button>
        </div>
      )}

      {isOpen && (
        <div
          className="theme-marketing marketing-app fixed inset-0 z-[11000] flex items-end lg:hidden"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/50" />

          <div
            className="relative max-h-[85vh] w-full overflow-y-auto rounded-t-2xl border-t border-m-border-light bg-m-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-m-border-light bg-m-surface px-4 py-3">
              <h3 className="text-sm font-medium text-m-ink">Booking summary</h3>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1.5 text-m-ink-muted transition-colors hover:bg-m-canvas-light hover:text-m-ink"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-4">
              <div className="rounded-2xl border border-m-border-light bg-m-surface p-5">
                <div className="mb-3 flex items-center">
                  <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-m-ink">
                    <Camera className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-m-ink">
                      {vendor?.business_name || 'Vendor'}
                    </h3>
                    <div className="mt-0.5 flex items-center text-xs text-m-ink-muted">
                      <Star className="mr-1 h-3.5 w-3.5 fill-current text-m-ink" />
                      <span className="font-medium text-m-ink">
                        {typeof serviceRating === 'number' && !isNaN(serviceRating)
                          ? serviceRating.toFixed(1)
                          : '0.0'}
                      </span>
                      <span className="ml-1 text-m-ink-faint">({serviceReviews})</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-m-ink-muted">
                  <div className="flex items-center">
                    <MapPin className="mr-2 h-3.5 w-3.5 text-m-ink" />
                    <span>
                      {vendor
                        ? `${vendor.city}, ${vendor.state}`
                        : 'Location not available'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Award className="mr-2 h-3.5 w-3.5 text-m-ink" />
                    <span>{category?.category_name || 'Service Category'}</span>
                  </div>
                  {vendor?.is_verified && (
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-m-border-light bg-m-canvas-light px-2.5 py-1">
                      <CheckCircle className="h-3.5 w-3.5 text-m-ink" strokeWidth={2.5} />
                      <span className="text-xs font-medium text-m-ink">Verified vendor</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 border-t border-m-border-light pt-4">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="rounded-xl bg-m-canvas-light p-3">
                      <div className="text-sm font-medium text-m-ink">
                        {money(service?.base_price || 0)}
                      </div>
                      <div className="mt-0.5 text-xs text-m-ink-faint">Starting price</div>
                    </div>
                    <div className="rounded-xl bg-m-canvas-light p-3">
                      <div className="text-sm font-medium text-m-ink">
                        {service?.min_duration_hours
                          ? `${service.min_duration_hours}-${
                              service.max_duration_hours || service.min_duration_hours
                            }h`
                          : 'Flexible'}
                      </div>
                      <div className="mt-0.5 text-xs text-m-ink-faint">Duration</div>
                    </div>
                  </div>
                </div>
              </div>

              {bookingData.selectedPackage && (
                <div className="rounded-2xl border border-m-border-light bg-m-surface p-5">
                  <h3 className="mb-3 text-sm font-medium text-m-ink">Booking summary</h3>
                  <div className="space-y-1.5">
                    <div className="flex items-start justify-between gap-3 border-b border-m-border-light py-2">
                      <span className="shrink-0 text-xs text-m-ink-muted">Package</span>
                      <span className="min-w-0 flex-1 break-words text-right text-xs font-medium leading-snug text-m-ink">
                        {bookingData.selectedPackage.name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-m-border-light py-2">
                      <span className="text-xs text-m-ink-muted">Base price</span>
                      <span className="text-xs font-medium text-m-ink">
                        {money(bookingData.selectedPackage.price || 0)}
                      </span>
                    </div>
                    {parseInt(bookingData.duration) >
                      parseInt(bookingData.selectedPackage.duration_hours || 4) && (
                      <div className="flex items-center justify-between border-b border-m-border-light py-2">
                        <span className="text-xs text-m-ink-muted">Extra hours</span>
                        <span className="text-xs font-medium text-m-ink">
                          {money(
                            (parseInt(bookingData.duration) -
                              parseInt(bookingData.selectedPackage.duration_hours || 4)) *
                              50
                          )}
                        </span>
                      </div>
                    )}
                    {appliedDiscount && appliedDiscount.discount_amount > 0 && (
                      <div className="flex items-center justify-between border-b border-m-border-light py-2">
                        <span className="text-xs text-m-ink-muted">
                          Discount ({appliedDiscount.code})
                        </span>
                        <span className="text-xs font-medium text-m-ink">
                          -{money(appliedDiscount.discount_amount)}
                        </span>
                      </div>
                    )}
                    {commissionSetting &&
                      commissionSetting.is_active &&
                      calculateCommission() > 0 && (
                        <div className="flex items-center justify-between border-b border-m-border-light py-2">
                          <span className="text-xs text-m-ink-muted">Service charge</span>
                          <span className="text-xs font-medium text-m-ink">
                            {money(calculateCommission())}
                            {commissionSetting.commission_type === 'percentage' && (
                              <span className="ml-1 text-xs text-m-ink-faint">
                                ({commissionSetting.commission_rate}%)
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    {showTaxLine && (
                      <div className="flex items-center justify-between border-b border-m-border-light py-2">
                        <span className="text-xs text-m-ink-muted">Tax</span>
                        <span className="text-xs font-medium text-m-ink">
                          {money(calculateTax?.() ?? 0)}
                        </span>
                      </div>
                    )}
                    <div className="mt-3 border-t border-m-border-light pt-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs text-m-ink-muted">
                          Deposit {paymentTerms ? `(${paymentTerms.depositLabel})` : '(50%)'}
                        </span>
                        <span className="text-xs font-medium text-m-ink">
                          {money(calculateDepositAmount())}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-full bg-m-ink px-4 py-3">
                        <span className="text-xs font-medium text-white/80">Due now</span>
                        <span className="text-base font-medium text-white">
                          {money(calculateDepositAmount())}
                        </span>
                      </div>
                      <div className="mt-2 text-center text-xs text-m-ink-faint">
                        Full total: {money(calculateTotal())}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBookingSidebar;
